import React from "react";
import "./App.css";
import Helmet from "react-helmet";
import { Link } from "react-scroll";
import { ReactComponent as AppleStoreSVG } from "./assets/AppleStoreSVG.svg";
import { ReactComponent as GooglePlaySVG } from "./assets/GooglePlaySVG.svg";
import { ReactComponent as TwitterIcon } from "./assets/twitter-icon.svg";
import { ReactComponent as LinkedInIcon } from "./assets/linkedin-icon.svg";
import { ReactComponent as InstagramIcon } from "./assets/instagram-icon.svg";
import logoCC from "./assets/logo_cc.png";
import heroImage from "./assets/marketing/hero-translation-app.png";
import globalImage from "./assets/marketing/global-communication.png";
import cameraDocumentImage from "./assets/marketing/camera-document-translation.png";

const siteUrl = "https://chatculture.app";
const appStoreUrl = "https://itunes.apple.com/us/app/6446968637";
const playStoreUrl =
  "https://play.google.com/store/apps/details?id=com.oneyerge.reactnativefirebase";
const pageTitle =
  "ChatCulture Translation App | Chat, Camera, AR and Document Translation";
const pageDescription =
  "Download ChatCulture, a translation app for multilingual chat, camera translation, document translation, AR translation, and language practice across 77+ languages.";

const featureCards = [
  {
    title: "Chat translation",
    text: "Send and receive translated messages naturally while keeping the original message one tap away.",
  },
  {
    title: "Camera and AR translation",
    text: "Translate signs, labels, menus, and surroundings from your camera when travel or daily life moves fast.",
  },
  {
    title: "Document translation",
    text: "Understand important documents and shared text without switching between separate tools.",
  },
  {
    title: "Original message access",
    text: "Review the source text behind translated messages so important meaning stays clear.",
  },
  {
    title: "Language practice",
    text: "Build confidence with guided practice and keep translations nearby while you learn.",
  },
  {
    title: "77+ languages",
    text: "Communicate across a broad set of languages with automatic language detection built for global conversations.",
  },
];

function StoreButtons({ compact = false }) {
  return (
    <div className={compact ? "store-buttons store-buttons-compact" : "store-buttons"}>
      <a
        href={appStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Download ChatCulture on the App Store"
      >
        <AppleStoreSVG className="store-icon" aria-hidden="true" focusable="false" />
      </a>
      <a
        href={playStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Get ChatCulture on Google Play"
      >
        <GooglePlaySVG className="store-icon" aria-hidden="true" focusable="false" />
      </a>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <Helmet>
        <html lang="en" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
        content="translation app, chat translation app, camera translation, document translation, AR translation, language communication app, multilingual messaging"
        />
        <link rel="canonical" href={`${siteUrl}/`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="ChatCulture" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={`${siteUrl}/og-chatculture.png`} />
        <meta property="og:url" content={`${siteUrl}/`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${siteUrl}/og-chatculture.png`} />
      </Helmet>

      <header className="site-header">
        <div className="nav-shell">
          <a href="/" className="brand-link" aria-label="ChatCulture home">
            <img src={logoCC} alt="ChatCulture logo" className="logo-img" />
            <span>ChatCulture</span>
          </a>
          <nav className="nav-links" aria-label="Main navigation">
            <Link to="video" smooth duration={250} offset={-88} className="nav-link">
              Video
            </Link>
            <Link to="features" smooth duration={250} offset={-88} className="nav-link">
              Features
            </Link>
            <Link to="download" smooth duration={250} offset={-88} className="nav-link">
              Download
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero-section" aria-labelledby="hero-heading">
          <div className="hero-copy">
            <p className="eyebrow">Translation app for multilingual messaging</p>
            <h1 id="hero-heading">ChatCulture Translation App</h1>
            <p className="hero-lede">
              Message, scan, read, and understand more of the world with
              translation tools built for everyday communication across 77+
              languages.
            </p>
            <StoreButtons />
            <div className="hero-proof" aria-label="ChatCulture highlights">
              <span>Chat translation</span>
              <span>Camera and AR</span>
              <span>Document translation</span>
            </div>
          </div>
          <div className="hero-visual">
            <img
              src={heroImage}
              alt="Phone mockup showing multilingual chat translation in the ChatCulture app"
              width="1693"
              height="929"
              loading="eager"
              fetchpriority="high"
            />
          </div>
        </section>

        <section className="video-section" id="video" aria-labelledby="video-heading">
          <div className="section-heading">
            <p className="eyebrow">See the app</p>
            <h2 id="video-heading">Translation that fits into daily communication</h2>
          </div>
          <div className="iframe-wrapper">
            <iframe
              src="https://www.youtube.com/embed/wycsif04u6I?si=VGqX0hW1x8_j-qTP"
              title="ChatCulture introduction video"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </section>

        <section className="split-section" aria-labelledby="connect-heading">
          <div className="section-media">
            <img
              src={globalImage}
              alt="Global communication illustration with translated message cards and language bubbles"
              width="1536"
              height="1024"
              loading="lazy"
            />
          </div>
          <div className="section-copy">
            <p className="eyebrow">Built for global connection</p>
            <h2 id="connect-heading">Keep messages clear across languages</h2>
            <p>
              ChatCulture helps reduce language friction when friends, family,
              travel, work, or language learning cross language barriers.
            </p>
            <ul className="check-list">
              <li>Automatic language detection for faster message translation.</li>
              <li>Original message access for clarity and trust.</li>
              <li>Simple mobile-first design for repeat daily use.</li>
            </ul>
          </div>
        </section>

        <section className="features-section" id="features" aria-labelledby="features-heading">
          <div className="section-heading">
            <p className="eyebrow">Top features</p>
            <h2 id="features-heading">One app for chat, camera, AR, and document translation</h2>
          </div>
          <div className="feature-grid">
            {featureCards.map((feature) => (
              <article className="feature-card" key={feature.title}>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="split-section split-section-reverse" aria-labelledby="scan-heading">
          <div className="section-copy">
            <p className="eyebrow">Scan, read, understand</p>
            <h2 id="scan-heading">Translate the world around you</h2>
            <p>
              Camera, AR, and document translation help make signs, menus,
              labels, files, and shared text easier to understand on the spot.
            </p>
            <Link to="download" smooth duration={300} offset={-88} className="text-link">
              Download ChatCulture
            </Link>
          </div>
          <div className="section-media">
            <img
              src={cameraDocumentImage}
              alt="Phone mockup scanning a document and sign with translation overlays"
              width="1659"
              height="948"
              loading="lazy"
            />
          </div>
        </section>

        <section className="download-section" id="download" aria-labelledby="download-heading">
          <div className="download-panel">
            <p className="eyebrow">Download the app</p>
            <h2 id="download-heading">Start translating messages today</h2>
            <p>
              Get ChatCulture on iOS or Android and communicate across languages
              with chat, camera, AR, document, and language-practice tools.
            </p>
            <StoreButtons compact />
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-columns">
          <div className="footer-column">
            <h2>About ChatCulture</h2>
            <p>
              ChatCulture is a language translation app for everyday global
              communication across 77+ languages.
            </p>
          </div>
          <div className="footer-column">
            <h2>Quick Links</h2>
            <ul>
              <li>
                <a href="/terms">Terms and Conditions</a>
              </li>
              <li>
                <a href="/privacy">Privacy Policy</a>
              </li>
              <li>
                <a href="mailto:chatcultureapp@gmail.com">Contact Us</a>
              </li>
            </ul>
          </div>
          <div className="footer-column">
            <h2>Follow Us</h2>
            <div className="social-icons">
              <a
                href="https://twitter.com/chatcultureapp"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow ChatCulture on X"
              >
                <TwitterIcon aria-hidden="true" focusable="false" />
              </a>
              <a
                href="https://www.linkedin.com/company/chatcultureapp"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow ChatCulture on LinkedIn"
              >
                <LinkedInIcon aria-hidden="true" focusable="false" />
              </a>
              <a
                href="https://www.instagram.com/chatcultureapp/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow ChatCulture on Instagram"
              >
                <InstagramIcon aria-hidden="true" focusable="false" />
              </a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <span>&copy; {new Date().getFullYear()} ChatCulture. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
